import { addDays, format } from 'date-fns';
import { DATE_RANGE_META, RANKED_KEYWORDS_METRIC, RANKED_PAGES_METRIC } from '../../constants/formMeta/growthTrends';
import { extractSegmentFilterParams, getCurrentDateFromCacheRangeWhenRefresh, resolveTrendMetrics } from '../common';

const CUSTOM_LABELS = new Set(['custom', 'custom date range']);

export const COMPETITIVE_TABLE_SECONDARY_METRICS = [RANKED_KEYWORDS_METRIC.value, RANKED_PAGES_METRIC.value];

export function needsSplitCompetitiveSecondaryMetric(metric2) {
  return COMPETITIVE_TABLE_SECONDARY_METRICS.includes(metric2);
}

// How long a saved trend waits for segment/date hydration before loading anyway.
export const SAVED_TREND_HYDRATION_FALLBACK_MS = 1500;

/**
 * Monotonic id for explore/trends calls. A response may be applied only when
 * its id is still the latest one issued, so an earlier in-flight (or cached)
 * payload cannot overwrite a later hydration-complete fetch.
 */
export function createChartRequestGuard() {
  let current = 0;
  return {
    begin() {
      current += 1;
      return current;
    },
    isCurrent(id) {
      return id === current;
    },
    current() {
      return current;
    },
  };
}

export function isRelativeDatePresetLabel(label) {
  if (typeof label !== 'string') return false;
  const trimmed = label.trim();
  if (!trimmed || CUSTOM_LABELS.has(trimmed.toLowerCase())) return false;
  return DATE_RANGE_META.some((option) => option.label === trimmed);
}

export function resolveTopTrendPickerSelection(selected, customRange) {
  if (selected && isRelativeDatePresetLabel(selected.label)) {
    return selected;
  }
  return customRange;
}

export function formatTrendPeriod(startDate, endDate) {
  const start = explicitDate(startDate, new Date());
  const end = explicitDate(endDate, start);
  return `${format(start, 'yyyy/MM/dd')} to ${format(addDays(end, 1), 'yyyy/MM/dd')}`;
}

function explicitDate(value, fallback) {
  if (!value) return fallback;
  const parsed = value instanceof Date ? value : new Date(value);
  return Number.isNaN(parsed.getTime()) ? fallback : parsed;
}

/**
 * Relative presets ("Last 12 Complete Weeks") are recomputed from today so the
 * picker label and the query window agree. Trends saved before the preset was
 * persisted keep their explicit dates and the Custom label.
 */
export function resolveReopenDateRanges(date = {}, now = new Date()) {
  const selection = date.selection || {};
  const comparison = date.comparison || {};
  const presetLabel = selection.label || selection.preset || date.date_range_preset || '';
  const comparisonLabel = comparison.label || 'Previous Period';

  const explicitSelected = {
    startDate: explicitDate(selection.start_date, now),
    endDate: explicitDate(selection.end_date, now),
    label: 'Custom',
  };
  const explicitComparison = {
    startDate: explicitDate(comparison.start_date, now),
    endDate: explicitDate(comparison.end_date, now),
    label: comparison.start_date ? 'Custom' : 'Previous Period',
  };

  if (!isRelativeDatePresetLabel(presetLabel)) {
    return {
      selected: explicitSelected,
      comparison: explicitComparison,
      usedPreset: false,
    };
  }

  const refreshed = getCurrentDateFromCacheRangeWhenRefresh({
    selected: {
      label: presetLabel,
      startDate: explicitSelected.startDate,
      endDate: explicitSelected.endDate,
    },
    comparison: {
      label: comparisonLabel,
      startDate: explicitComparison.startDate,
      endDate: explicitComparison.endDate,
    },
  });

  const resolvedComparisonLabel =
    comparisonLabel === 'Previous Year Same Period' || comparisonLabel === 'Previous Period'
      ? comparisonLabel
      : 'Previous Period';
  // A custom comparison is a fixed window the user picked; only the selected
  // preset rolls forward.
  const keepCustomComparison = CUSTOM_LABELS.has(String(comparisonLabel).trim().toLowerCase()) && comparison.start_date;

  return {
    selected: {
      ...refreshed.selected,
      label: refreshed.selected.label || presetLabel,
      startDate: refreshed.selected.startDate || explicitSelected.startDate,
      endDate: refreshed.selected.endDate || explicitSelected.endDate,
    },
    comparison: keepCustomComparison
      ? { ...explicitComparison, label: 'Custom' }
      : {
          startDate: refreshed.comparison.startDate || explicitComparison.startDate,
          endDate: refreshed.comparison.endDate || explicitComparison.endDate,
          label: resolvedComparisonLabel,
        },
    usedPreset: true,
  };
}

function hasExplicitMarketSegment(segmentData) {
  const value = segmentData?.market_share_segment?.[0]?.value;
  return Boolean(value && value !== 'default');
}

/**
 * Saved competitive trends persist the market segment on filters.segment_id
 * and, when the user applied a named saved segment, on segmentData.saved_segment.
 * Reopen must put both back onto the segment selector and the explore filters.
 */
export function hydrateSavedTrendSegments({ filters = {}, segmentData_1 = {}, segmentData_2 = {} } = {}) {
  const next1 = { ...(segmentData_1 || {}) };
  const next2 = { ...(segmentData_2 || {}) };

  const persistedSavedSegment = filters.saved_segment;
  if ((!Array.isArray(next1.saved_segment) || next1.saved_segment.length === 0) && persistedSavedSegment) {
    next1.saved_segment = Array.isArray(persistedSavedSegment) ? persistedSavedSegment : [persistedSavedSegment];
  }

  const savedDefinition = next1.saved_segment?.[0]?.definition?.segmentData_1;
  if (savedDefinition && typeof savedDefinition === 'object') {
    Object.keys(savedDefinition).forEach((key) => {
      const current = next1[key];
      const fromSaved = savedDefinition[key];
      const currentEmpty = !Array.isArray(current) || current.length === 0;
      if (currentEmpty && Array.isArray(fromSaved) && fromSaved.length > 0) {
        next1[key] = fromSaved;
      }
    });
  }

  const savedSegmentId = filters.segment_id;
  if (!hasExplicitMarketSegment(next1) && savedSegmentId && savedSegmentId !== 'default') {
    const storedName =
      filters.segment_name ||
      filters.market_share_segment_name ||
      next1.market_share_segment?.[0]?.text ||
      savedSegmentId;
    next1.market_share_segment = [
      {
        text: storedName,
        value: savedSegmentId,
        type: 'market_share_segment',
      },
    ];
  }

  return { segmentData_1: next1, segmentData_2: next2 };
}

/**
 * A user's segmentLimits must still apply to a saved trend, which may have
 * been saved by a teammate without those limits. Keep the saved selections
 * that fall inside the limit; when none do, fall back to the limit itself.
 */
export function constrainSavedSelectionsToLimit(savedSelections, limitValue, limitSelections) {
  const allowed = new Set(
    String(limitValue == null ? '' : limitValue)
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean)
  );
  const saved = Array.isArray(savedSelections) ? savedSelections : savedSelections ? [savedSelections] : [];
  const kept = saved.filter((selection) => selection && allowed.has(String(selection.value)));
  if (kept.length) {
    return { selections: kept, value: kept.map((selection) => selection.value).join(',') };
  }
  return { selections: limitSelections, value: limitValue };
}

export function isSavedTrendHydrationComplete({
  isTopTrendDetails = false,
  isMarketShare = false,
  filters = {},
  hasDomain = false,
  trendHydrationReady = true,
  hydrationTimedOut = false,
} = {}) {
  if (isMarketShare && !hasDomain) return false;
  if (!isTopTrendDetails) return true;
  if (!trendHydrationReady) return false;
  // After the fallback window, load with whatever filters landed rather than
  // leave the chart spinning forever on a segment or date that never arrives.
  if (hydrationTimedOut) return true;
  if (!filters.metric_1 || !filters.selected_period) return false;
  const segmentId = filters.segment_id;
  if (segmentId && String(segmentId) !== 'default') {
    // The explore call reads market_share_segmentA. It can legitimately differ
    // from segment_id (multi-select, or a segment restored from its saved
    // definition), so only wait for it to be present.
    if (!filters.market_share_segmentA) return false;
  }
  return true;
}

/**
 * Goal relabel must follow the current metric_1 / metric_2 keys. A previous
 * metric1Type ("Bounce Rate", "Clicks") left on filters or state is not a
 * label for the metric the user just selected.
 */
export function resolveCurrentTrendMetricLabels({
  filters = {},
  segmentData_1,
  summaryMetrics,
  isMarketShare = false,
  goalsConcat,
} = {}) {
  const filtersWithoutLabels = { ...(filters || {}) };
  delete filtersWithoutLabels.metric1Type;
  delete filtersWithoutLabels.metric2Type;
  return resolveTrendMetrics(
    {
      filters: filtersWithoutLabels,
      segmentData_1,
      summaryMetrics,
      isMarketShare,
    },
    { isMarketShare, goalsConcat }
  );
}

const escapeRegExp = (text) => String(text).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const capitalizeFirst = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text);

/**
 * Goal names can arrive after the summary cards were built with the
 * placeholder label ("Goal 1"). Swap old labels for new ones in each card's
 * metric and title. Longer labels go first so "Goal 1 Conversion Rate" is not
 * half-replaced by "Goal 1", and "Goal 1" never matches inside "Goal 10".
 */
export function relabelSummaryPanel(summaryPanel, replacements = []) {
  const pairs = replacements
    .filter(([from, to]) => from && to && from !== to)
    .sort(([a], [b]) => String(b).length - String(a).length);
  if (!summaryPanel || !pairs.length) return summaryPanel;
  const relabelTitle = (title) =>
    pairs.reduce(
      (text, [from, to]) =>
        text.replace(new RegExp(`${escapeRegExp(capitalizeFirst(from))}(?!\\d)`, 'g'), () => capitalizeFirst(to)),
      String(title)
    );
  let changed = false;
  const next = {};
  Object.keys(summaryPanel).forEach((key) => {
    const measure = summaryPanel[key];
    if (!measure || typeof measure !== 'object') {
      next[key] = measure;
      return;
    }
    const pair = pairs.find(([from]) => measure.metric === from);
    const metric = pair ? pair[1] : measure.metric;
    const title = typeof measure.title === 'string' ? relabelTitle(measure.title) : measure.title;
    if (metric !== measure.metric || title !== measure.title) {
      changed = true;
      next[key] = { ...measure, metric, title };
    } else {
      next[key] = measure;
    }
  });
  return changed ? next : summaryPanel;
}

export function chartSeriesFromSummaryRows(rows) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return { chartsData: [], summaryRow: null };
  }
  return {
    chartsData: rows.slice(0, -1),
    summaryRow: rows[rows.length - 1],
  };
}

export function applyChartResponse({ guard, requestId, rows }) {
  if (!guard || !guard.isCurrent(requestId)) {
    return { applied: false, reason: 'stale', chartsData: null, summaryRow: null };
  }
  const { chartsData, summaryRow } = chartSeriesFromSummaryRows(rows);
  return { applied: true, reason: 'current', chartsData, summaryRow };
}

/**
 * Market Share + Ranked Keywords/Pages is not a valid dual-metric slot on
 * site_report_by_site_pop_market (explore rejects TSS and the page toasts).
 * Each metric is valid alone as metric_1 in the SSS shape. Rewrite a prepared
 * TSS body into that single-metric request.
 */
export function toSingleMetricPayload(payload, view, metricValue) {
  const next = JSON.parse(JSON.stringify(payload || {}));
  next.filters = { ...(next.filters || {}) };
  next.filters[`${view}.metric_1`] = metricValue;
  next.filters[`${view}.metric_2`] = 'none';
  next.filters[`${view}.metric_measure_1`] = 'metric1^_selectPeriod';
  next.filters[`${view}.metric_measure_2`] = 'metric1^_lastPeriod';
  next.filters[`${view}.metric_measure_3`] = 'metric1^_deltaValue';
  next.filters[`${view}.metric_measure_4`] = 'metric1^_deltaPercentage';
  delete next.filters[`${view}.metric_measure_5`];
  delete next.filters[`${view}.metric_measure_6`];
  if (Array.isArray(next.fields)) {
    next.fields = next.fields.filter(
      (field) => !String(field).endsWith('.measure_5_wrapper') && !String(field).endsWith('.measure_6_wrapper')
    );
  }
  return next;
}

/**
 * SSS rows: measure_1 select, measure_2 last period, measure_4 delta %.
 * TSS rows the chart expects: measure_1 metric1, measure_2 metric2,
 * measure_3 metric1 delta %, measure_4 metric2 delta %.
 */
export function mergeDualSingleMetricRows(primaryRows, secondaryRows, keyPrefix = '') {
  const primary = Array.isArray(primaryRows) ? primaryRows : [];
  const secondary = Array.isArray(secondaryRows) ? secondaryRows : [];
  const key = (name) => `${keyPrefix}${name}`;
  // The last row of each response is the period total and has no timeframe.
  // Pair totals with totals; weekly rows only by timeframe, so a week missing
  // from one response cannot pick up the other response's total.
  const lastPrimary = primary.length - 1;
  const lastSecondary = secondary.length - 1;
  const secondaryByTime = new Map();
  secondary.slice(0, Math.max(lastSecondary, 0)).forEach((row) => {
    if (row && row[key('dynamic_timeframe')]) secondaryByTime.set(row[key('dynamic_timeframe')], row);
  });
  const sameLength = primary.length === secondary.length;
  const findOther = (row, index) => {
    if (index === lastPrimary) return secondary[lastSecondary] || {};
    const timeKey = row && row[key('dynamic_timeframe')];
    if (timeKey) return secondaryByTime.get(timeKey) || {};
    return (sameLength && secondary[index]) || {};
  };

  return primary.map((row, index) => {
    const other = findOther(row, index);
    return {
      ...row,
      [key('measure_1_wrapper')]: row[key('measure_1_wrapper')],
      [key('measure_2_wrapper')]: other[key('measure_1_wrapper')],
      [key('measure_3_wrapper')]: row[key('measure_4_wrapper')],
      [key('measure_4_wrapper')]: other[key('measure_4_wrapper')],
      [key('measure_5_wrapper')]: row[key('measure_2_wrapper')],
      [key('measure_6_wrapper')]: other[key('measure_2_wrapper')],
    };
  });
}

/**
 * Previous-period series the chart draws behind the selected period, read
 * from the compared_timeframe and measure_5/6 (last period) columns.
 */
export function previousPeriodRowsFromChartRows(rows) {
  return (Array.isArray(rows) ? rows : []).map((record) => ({
    dynamic_timeframe: record.compared_timeframe,
    measure_1_wrapper: record.measure_5_wrapper,
    measure_2_wrapper: record.measure_6_wrapper,
    measure_3_wrapper: record.measure_7_wrapper,
    measure_4_wrapper: record.measure_8_wrapper,
    data: [],
  }));
}

/**
 * Fields getTrendDefination writes into trend_definition. Kept pure so a test
 * can save, then reopen, without mounting GrowthTrends.
 */
export function buildSavedTrendDefinition({
  filters = {},
  metric1Type,
  metric2Type,
  dateRanges,
  segmentData_1 = {},
  segmentData_2 = {},
  summaryMetrics = [],
  isMarketShare = false,
} = {}) {
  const selected = dateRanges?.selected || {};
  const comparison = dateRanges?.comparison || {};
  const marketSegment = segmentData_1?.market_share_segment?.[0];
  const savedFilters = {
    is_date_comparison: filters.is_date_comparison,
    metric_1: filters.metric_1,
    metric_2: filters.metric_2,
    metric1Type,
    metric2Type,
    date_granularity: filters.date_granularity,
    redirectToBQ: Boolean(filters.redirectToBQ),
  };

  if (isMarketShare) {
    savedFilters.segment_id = marketSegment?.value || filters.market_share_segmentA || 'default';
    if (marketSegment?.text) {
      savedFilters.segment_name = marketSegment.text;
    }
  }
  if (Array.isArray(segmentData_1?.saved_segment) && segmentData_1.saved_segment.length) {
    savedFilters.saved_segment = segmentData_1.saved_segment;
  }

  return {
    date: {
      selection: {
        start_date: selected.startDate ? new Date(selected.startDate).toISOString() : undefined,
        end_date: selected.endDate ? new Date(selected.endDate).toISOString() : undefined,
        label: selected.label || 'Custom',
      },
      comparison:
        filters.is_date_comparison === 'Yes'
          ? {
              start_date: comparison.startDate ? new Date(comparison.startDate).toISOString() : undefined,
              end_date: comparison.endDate ? new Date(comparison.endDate).toISOString() : undefined,
              label: comparison.label || 'Custom',
            }
          : { label: comparison.label || 'Previous Period' },
    },
    filters: savedFilters,
    segmentData_1,
    segmentData_2,
    summaryMetrics,
    isMarketShare,
  };
}

export function reopenSavedTrend(definition = {}, { summaryPanelData, goalsConcat, isMarketShare = false } = {}) {
  const segments = hydrateSavedTrendSegments({
    filters: definition.filters || {},
    segmentData_1: definition.segmentData_1 || {},
    segmentData_2: definition.segmentData_2 || {},
  });
  const dates = resolveReopenDateRanges(definition.date || {});
  const segmentFilters = extractSegmentFilterParams({
    segmentData_1: segments.segmentData_1,
    segmentData_2: segments.segmentData_2,
  });
  const metrics = resolveTrendMetrics(
    {
      ...definition,
      filters: definition.filters || {},
      segmentData_1: segments.segmentData_1,
      segmentData_2: segments.segmentData_2,
      isMarketShare,
    },
    { isMarketShare, goalsConcat, summaryPanelData }
  );

  return {
    ...segments,
    ...metrics,
    usedPreset: dates.usedPreset,
    dateRanges: {
      selected: dates.selected,
      comparison: dates.comparison,
    },
    filters: {
      ...(definition.filters || {}),
      ...segmentFilters,
      selected_period: formatTrendPeriod(dates.selected.startDate, dates.selected.endDate),
      ...(definition.filters?.is_date_comparison === 'Yes'
        ? { comparison_period: formatTrendPeriod(dates.comparison.startDate, dates.comparison.endDate) }
        : {}),
    },
  };
}
